﻿import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Badge, Text, Layout,
  BlockStack, InlineStack, DataTable, Modal, TextField,
  Checkbox, Spinner, Box, Tooltip, CalloutCard,
} from '@shopify/polaris';
import { useApiKeys } from '../../hooks/useApiKeys';

// ── Revealed key banner ───────────────────────────────────────────────────────

interface KeyRevealBannerProps {
  plaintext: string;
  onDismiss: () => void;
}

function KeyRevealBanner({ plaintext, onDismiss }: KeyRevealBannerProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(plaintext);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Banner
      title="Your API key — copy it now"
      tone="warning"
      onDismiss={onDismiss}
    >
      <BlockStack gap="200">
        <Text as="p" variant="bodySm">
          This key will only be shown <strong>once</strong>. Store it securely.
        </Text>
        <InlineStack gap="200" align="start" blockAlign="center">
          <code style={{ fontFamily: 'monospace', background: '#f4f4f4', padding: '4px 8px', borderRadius: 4 }}>
            {plaintext}
          </code>
          <Button size="slim" onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </InlineStack>
      </BlockStack>
    </Banner>
  );
}

// ── Create key modal ──────────────────────────────────────────────────────────

interface CreateModalProps {
  open: boolean;
  loading: boolean;
  availableScopes: { value: string; label: string }[];
  onClose: () => void;
  onCreate: (name: string, scopes: string[]) => Promise<any>;
}

function CreateKeyModal({ open, loading, availableScopes, onClose, onCreate }: CreateModalProps) {
  const [name, setName]               = useState('');
  const [selectedScopes, setSelected] = useState<string[]>([]);
  const [nameError, setNameError]     = useState('');
  const [scopeError, setScopeError]   = useState('');

  const toggleScope = (value: string) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value],
    );
  };

  const handleSubmit = async () => {
    setNameError('');
    setScopeError('');
    if (!name.trim())            { setNameError('Key name is required.'); return; }
    if (selectedScopes.length === 0) { setScopeError('Select at least one scope.'); return; }

    const result = await onCreate(name.trim(), selectedScopes);
    if (result) {
      setName('');
      setSelected([]);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create API Key"
      primaryAction={{ content: 'Create Key', loading, onAction: handleSubmit }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <TextField
            label="Key name"
            value={name}
            onChange={setName}
            placeholder="e.g. My Integration"
            autoComplete="off"
            error={nameError}
          />

          <BlockStack gap="200">
            <Text as="p" variant="bodyMd" fontWeight="semibold">Scopes</Text>
            {scopeError && <Text as="p" variant="bodySm" tone="critical">{scopeError}</Text>}
            {availableScopes.map((s) => (
              <Checkbox
                key={s.value}
                label={s.label}
                checked={selectedScopes.includes(s.value)}
                onChange={() => toggleScope(s.value)}
              />
            ))}
          </BlockStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

export function ApiKeysPanel() {
  const {
    keys, scopes, loading, actionId, error, newKey,
    fetchKeys, create, rotate, revoke, reactivate, remove, dismissNewKey, setError,
  } = useApiKeys();

  const [showCreate, setShowCreate]   = useState(false);
  const [deleteId, setDeleteId]       = useState<number | null>(null);
  const keyToDelete = keys.find((k) => k.id === deleteId);

  useEffect(() => { fetchKeys(); }, []);

  const rows = keys.map((k) => [
    <BlockStack gap="100">
      <Text as="span" variant="bodyMd" fontWeight="semibold">{k.name}</Text>
      <code style={{ fontFamily: 'monospace', fontSize: 12, color: '#637381' }}>{k.key_prefix}…</code>
    </BlockStack>,
    <InlineStack gap="100" wrap>
      {k.scopes.map((s) => (
        <Badge key={s} tone="info">{s}</Badge>
      ))}
    </InlineStack>,
    k.is_active
      ? <Badge tone="success">Active</Badge>
      : <Badge tone="critical">Revoked</Badge>,
    k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : 'Never',
    new Date(k.created_at).toLocaleDateString(),
    <InlineStack gap="200">
      <Tooltip content="Generate a new secret for this key (old key stops working immediately)">
        <Button
          size="slim"
          loading={actionId === k.id}
          disabled={actionId !== null && actionId !== k.id}
          onClick={() => rotate(k.id)}
        >
          Rotate
        </Button>
      </Tooltip>
      {k.is_active ? (
        <Tooltip content="Temporarily disable this key without deleting it — integrations using it will stop working until reactivated">
          <Button
            size="slim"
            loading={actionId === k.id}
            disabled={actionId !== null && actionId !== k.id}
            onClick={() => revoke(k.id)}
          >
            Revoke
          </Button>
        </Tooltip>
      ) : (
        <Button
          size="slim"
          loading={actionId === k.id}
          disabled={actionId !== null && actionId !== k.id}
          onClick={() => reactivate(k.id)}
        >
          Reactivate
        </Button>
      )}
      <Button
        size="slim"
        tone="critical"
        variant="plain"
        loading={actionId === k.id}
        disabled={actionId !== null && actionId !== k.id}
        onClick={() => setDeleteId(k.id)}
      >
        Delete
      </Button>
    </InlineStack>,
  ]);

  return (
    <Page fullWidth
      title="Public API Keys"
      subtitle="Manage API keys for programmatic access to your store data"
      primaryAction={{
        content: 'Create API Key',
        onAction: () => setShowCreate(true),
        disabled: keys.length >= 10,
      }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          {newKey && (
            <KeyRevealBanner
              plaintext={newKey.plaintext_key}
              onDismiss={dismissNewKey}
            />
          )}

          <CalloutCard
            title="Public API Documentation"
            illustration="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            primaryAction={{ content: 'View docs', url: '#' }}
          >
            <Text as="p" variant="bodyMd">
              Use API keys to access your store data from external tools and integrations.
              Base URL: <code>/api/public/v1/</code>. Include the key as a{' '}
              <code>Authorization: Bearer &lt;key&gt;</code> header.
            </Text>
          </CalloutCard>

          {loading && keys.length === 0 ? (
            <Card>
              <InlineStack align="center"><Spinner /></InlineStack>
            </Card>
          ) : keys.length === 0 ? (
            <Card>
              <Box padding="800">
                <BlockStack gap="400" align="center" inlineAlign="center">
                  <Text as="h3" variant="headingMd">No API keys yet</Text>
                  <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                    Create an API key to start integrating with external tools.
                  </Text>
                  <Button variant="primary" onClick={() => setShowCreate(true)}>
                    Create your first key
                  </Button>
                </BlockStack>
              </Box>
            </Card>
          ) : (
            <Card padding="0">
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Key', 'Scopes', 'Status', 'Last Used', 'Created', 'Actions']}
                rows={rows}
              />
            </Card>
          )}
          </BlockStack>
        </Layout.Section>
      </Layout>

      <CreateKeyModal
        open={showCreate}
        loading={loading}
        availableScopes={scopes}
        onClose={() => setShowCreate(false)}
        onCreate={create}
      />

      <Modal
        open={deleteId !== null}
        onClose={() => setDeleteId(null)}
        title="Delete API Key"
        primaryAction={{
          content: 'Yes, Delete',
          destructive: true,
          loading: actionId === deleteId,
          onAction: async () => {
            if (deleteId !== null) {
              await remove(deleteId);
              setDeleteId(null);
            }
          },
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setDeleteId(null) }]}
      >
        <Modal.Section>
          <Text as="p" variant="bodyMd">
            Delete <strong>{keyToDelete?.name}</strong> ({keyToDelete?.key_prefix}…)?
            Any integrations using this key will immediately stop working.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
